// Shapes widget. Legacy: addShape + starPoints/regularPolygonPoints (L3986-4114).
// Fabric v7: named imports; Group uses the default fit-content layout (StaticLayout
// is reserved for barcode/QR/SVG groups in Phase 2 — see map §4.3).
import {
  Rect, Circle, Ellipse, Triangle, Line, Polygon, Path, Group, IText,
  type FabricObject, type XY,
} from 'fabric'
import { getCanvas } from './../state'
import { labelPx } from './../canvas'

export type ShapeType =
  | 'rect' | 'roundrect' | 'circle' | 'ellipse' | 'triangle'
  | 'line' | 'vline' | 'dline' | 'diamond' | 'pentagon' | 'hexagon'
  | 'octagon' | 'star' | 'star4' | 'heart' | 'arrow' | 'cross'
  | 'check' | 'xmark' | 'warning' | 'badge' | 'cloud' | 'bubble'

const DEFAULTS = { fill: 'transparent', stroke: '#0f172a', strokeWidth: 2, strokeUniform: true } as const

export function starPoints(points: number, outerR: number, innerR: number): XY[] {
  const pts: XY[] = []
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR
    const angle = (Math.PI / points) * i - Math.PI / 2
    pts.push({ x: outerR + r * Math.cos(angle), y: outerR + r * Math.sin(angle) })
  }
  return pts
}

export function regularPolygonPoints(sides: number, radius: number): XY[] {
  const pts: XY[] = []
  for (let i = 0; i < sides; i++) {
    const angle = ((2 * Math.PI) / sides) * i - Math.PI / 2
    pts.push({ x: radius + radius * Math.cos(angle), y: radius + radius * Math.sin(angle) })
  }
  return pts
}

export function addShape(type: ShapeType): FabricObject | undefined {
  const canvas = getCanvas()
  // Use logical label dimensions, NOT canvas.getWidth()/getHeight() — those return
  // the zoom-scaled display size, which would place shapes off-centre under zoom.
  const { w, h } = labelPx()
  const cx = w / 2
  const cy = h / 2
  const id = (p: string) => p + '-' + Date.now()
  let obj: FabricObject | undefined

  switch (type) {
    case 'rect':
      obj = new Rect({ ...DEFAULTS, left: cx - 40, top: cy - 25, width: 80, height: 50, id: id('rect') } as any); break
    case 'roundrect':
      obj = new Rect({ ...DEFAULTS, left: cx - 40, top: cy - 25, width: 80, height: 50, rx: 8, ry: 8, id: id('rrect') } as any); break
    case 'circle':
      obj = new Circle({ ...DEFAULTS, left: cx - 25, top: cy - 25, radius: 25, id: id('circle') } as any); break
    case 'ellipse':
      obj = new Ellipse({ ...DEFAULTS, left: cx - 40, top: cy - 20, rx: 40, ry: 20, id: id('ellipse') } as any); break
    case 'triangle':
      obj = new Triangle({ ...DEFAULTS, left: cx - 25, top: cy - 25, width: 50, height: 50, id: id('tri') } as any); break
    case 'line':
      obj = new Line([cx - 50, cy, cx + 50, cy], { stroke: '#0f172a', strokeWidth: 2, id: id('line') } as any); break
    case 'vline':
      obj = new Line([cx, cy - 40, cx, cy + 40], { stroke: '#0f172a', strokeWidth: 2, id: id('vline') } as any); break
    case 'dline':
      obj = new Line([cx - 40, cy + 30, cx + 40, cy - 30], { stroke: '#0f172a', strokeWidth: 2, id: id('dline') } as any); break
    case 'diamond':
      obj = new Polygon([{ x: 30, y: 0 }, { x: 60, y: 30 }, { x: 30, y: 60 }, { x: 0, y: 30 }], { ...DEFAULTS, left: cx - 30, top: cy - 30, id: id('diamond') } as any); break
    case 'pentagon':
      obj = new Polygon(regularPolygonPoints(5, 28), { ...DEFAULTS, left: cx - 28, top: cy - 28, id: id('pent') } as any); break
    case 'hexagon':
      obj = new Polygon(regularPolygonPoints(6, 28), { ...DEFAULTS, left: cx - 28, top: cy - 28, id: id('hex') } as any); break
    case 'octagon':
      obj = new Polygon(regularPolygonPoints(8, 28), { ...DEFAULTS, left: cx - 28, top: cy - 28, id: id('oct') } as any); break
    case 'star':
      obj = new Polygon(starPoints(5, 30, 14), { ...DEFAULTS, left: cx - 30, top: cy - 30, id: id('star') } as any); break
    case 'star4':
      obj = new Polygon(starPoints(4, 30, 12), { ...DEFAULTS, left: cx - 30, top: cy - 30, id: id('star4') } as any); break
    case 'heart':
      obj = new Path('M 30 55 C 30 55 5 40 5 20 C 5 10 15 2 25 10 L 30 16 L 35 10 C 45 2 55 10 55 20 C 55 40 30 55 30 55 Z', { ...DEFAULTS, left: cx - 30, top: cy - 30, id: id('heart') } as any); break
    case 'arrow':
      obj = new Polygon([{ x: 0, y: 12 }, { x: 50, y: 12 }, { x: 50, y: 0 }, { x: 70, y: 17 }, { x: 50, y: 34 }, { x: 50, y: 22 }, { x: 0, y: 22 }], { ...DEFAULTS, left: cx - 35, top: cy - 17, id: id('arrow') } as any); break
    case 'cross': {
      const w = 50, t = 16, hw = w / 2, ht = t / 2
      const pts = [
        { x: hw - ht, y: 0 }, { x: hw + ht, y: 0 }, { x: hw + ht, y: hw - ht }, { x: w, y: hw - ht }, { x: w, y: hw + ht }, { x: hw + ht, y: hw + ht },
        { x: hw + ht, y: w }, { x: hw - ht, y: w }, { x: hw - ht, y: hw + ht }, { x: 0, y: hw + ht }, { x: 0, y: hw - ht }, { x: hw - ht, y: hw - ht },
      ]
      obj = new Polygon(pts, { ...DEFAULTS, left: cx - hw, top: cy - hw, id: id('cross') } as any); break
    }
    case 'check':
      obj = new Path('M 5 28 L 20 43 L 55 8', { fill: 'transparent', stroke: '#0f172a', strokeWidth: 5, strokeLineCap: 'round', strokeLineJoin: 'round', left: cx - 30, top: cy - 25, id: id('check') } as any); break
    case 'xmark':
      obj = new Path('M 5 5 L 45 45 M 45 5 L 5 45', { fill: 'transparent', stroke: '#0f172a', strokeWidth: 5, strokeLineCap: 'round', left: cx - 25, top: cy - 25, id: id('xmark') } as any); break
    case 'warning': {
      const tri = new Triangle({ width: 60, height: 52, fill: 'transparent', stroke: '#0f172a', strokeWidth: 2, originX: 'center', originY: 'center' } as any)
      const bang = new IText('!', { fontSize: 28, fontWeight: 'bold', fontFamily: 'Inter', fill: '#0f172a', originX: 'center', originY: 'center', top: 4, selectable: false, evented: false } as any)
      obj = new Group([tri, bang], { left: cx - 30, top: cy - 26, id: id('warn') } as any); break
    }
    case 'badge': {
      const outer = new Circle({ radius: 30, fill: 'transparent', stroke: '#0f172a', strokeWidth: 2, originX: 'center', originY: 'center' } as any)
      const inner = new Circle({ radius: 18, fill: 'transparent', stroke: '#0f172a', strokeWidth: 2, originX: 'center', originY: 'center' } as any)
      obj = new Group([outer, inner], { left: cx - 30, top: cy - 30, id: id('badge') } as any); break
    }
    case 'cloud':
      obj = new Path('M 50 15 C 50 5 40 0 32 5 C 28 -2 18 -2 14 5 C 6 3 0 10 5 18 C 0 22 2 30 10 30 L 48 30 C 56 30 58 22 52 18 C 56 14 54 8 50 15 Z', { ...DEFAULTS, left: cx - 30, top: cy - 18, id: id('cloud') } as any); break
    case 'bubble':
      obj = new Path('M 10 5 C 5 5 0 10 0 18 C 0 26 5 30 10 30 L 12 38 L 20 30 L 50 30 C 55 30 60 26 60 18 C 60 10 55 5 50 5 Z', { ...DEFAULTS, left: cx - 30, top: cy - 20, id: id('bubble') } as any); break
  }

  if (obj) {
    canvas.add(obj)
    canvas.setActiveObject(obj)
    canvas.requestRenderAll()
  }
  return obj
}
